import { LOCAL_STORAGE_KEY_UI_LOCALE } from '@/ui-kit/config'
import { setDayjsLocale } from '@/utils/dayjs'
import { type Messages, i18n } from '@lingui/core'

type LocaleMeta = {
  label: string
  flag: string
  dir: 'ltr' | 'rtl'
}

export const DEFAULT_LOCALE = 'en'

export const SUPPORTED_LOCALES: Record<string, LocaleMeta> = {
  en: { label: 'English', flag: '🇺🇸', dir: 'ltr' },
  hu: { label: 'Magyar', flag: '🇭🇺', dir: 'ltr' },
  ro: { label: 'Română', flag: '🇷🇴', dir: 'ltr' },
  uk: { label: 'Українська', flag: '🇺🇦', dir: 'ltr' }
}

const catalogLoaders: Record<string, () => Promise<{ messages: Messages }>> = {
  en: () => import('@/locales/en/messages'),
  uk: () => import('@/locales/uk/messages'),
  hu: () => import('@/locales/hu/messages'),
  ro: () => import('@/locales/ro/messages')
}

function persistUiLocale(locale: string): void {
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY_UI_LOCALE, JSON.stringify(locale))
  } catch {
    return
  }
}

export function detectInitialLocale(): string {
  const urlLocale = new URLSearchParams(window.location.search).get('lng')
  if (urlLocale && catalogLoaders[urlLocale]) {
    persistUiLocale(urlLocale)
    return urlLocale
  }

  let stored: string | null = null
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY_UI_LOCALE)
    const parsed = raw ? JSON.parse(raw) : null
    if (typeof parsed === 'string') stored = parsed
  } catch {
    stored = null
  }

  if (stored && catalogLoaders[stored]) return stored

  const navigatorLocale = window.navigator.language?.split('-')[0]
  return navigatorLocale && catalogLoaders[navigatorLocale] ? navigatorLocale : DEFAULT_LOCALE
}

export async function dynamicActivate(locale: string): Promise<void> {
  const target = catalogLoaders[locale] ? locale : DEFAULT_LOCALE
  const { messages } = await catalogLoaders[target]()

  i18n.loadAndActivate({ locale: target, messages })
  setDayjsLocale(target)
  document.documentElement.lang = target
  document.documentElement.dir = SUPPORTED_LOCALES[target]?.dir ?? 'ltr'
}

export { i18n }
